import { formatStatistic } from './format-statistic';
import { useTranslation } from 'react-i18next';
import { useOddsFormat } from '../session/use-odds-format';
import { formatAge } from './format-age';
import { formatCurrency, formatNumber } from './format-number';
import { parseDecimalInput } from './decimal-input';
import { formatCalendarDay } from './format-calendar-date';
import { formatPrice } from './format-price';
import { formatRelativeTime } from './format-relative-time';
import { formatLocalTime, getLocalTimezone } from './local-time';
import { useFormatterTimezone } from './formatter-timezone';
import { useSession } from '../session/use-session';

export const useFormatters = () => {
  const { i18n } = useTranslation();
  const oddsFormat = useOddsFormat();
  const fixed = useFormatterTimezone();
  const session = useSession();
  const timezone = fixed?.timezone ?? session.user?.settings.timezone ?? getLocalTimezone();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const now =
    fixed?.now === undefined
      ? undefined
      : typeof fixed.now === 'string'
        ? Date.parse(fixed.now)
        : fixed.now;

  return {
    timezone,
    formatStatistic: (metric: string, value: number | null) =>
      formatStatistic(metric, value, locale),
    formatLocalTime: (value: string | Date, pattern?: string) =>
      formatLocalTime(value, locale, pattern, timezone),
    formatCalendarDay: (value: string, pattern?: string) =>
      formatCalendarDay(value, locale, pattern),
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
      formatNumber(value, locale, options),
    formatDecimalInput: (value: number, maximumFractionDigits = 2) =>
      formatNumber(value, locale, { useGrouping: false, maximumFractionDigits }),
    parseDecimalInput: (raw: string) => parseDecimalInput(raw, locale),
    formatCurrency: (value: number, currency = 'BRL') => formatCurrency(value, locale, currency),
    formatAge: (ms: number) => formatAge(ms, locale),
    formatRelativeTime: (iso: string) => formatRelativeTime(iso, locale, now),
    formatPrice: (decimal: number) => formatPrice(decimal, oddsFormat, locale),
  };
};
